import { Injectable } from '@angular/core';
import {
  ChatChannel,
  ChatMessageRole,
  generateChatAnswer$,
  GenerateChatAnswerResponse,
  InvocationContextAuthState,
  InvocationContextChannel,
  InvocationContextMedium,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { map, Observable, timeout } from 'rxjs';
import { A2pAnalysis, A2pAnalysisItem, A2pAnalysisSeverity } from './a2p-analysis.types';

const A2P_ASSISTANT_ID = 'ASSISTANT-A2P';
const SUBMIT_A2P_ANALYSIS_TOOL = 'SubmitA2PAnalysis';
const ANALYSIS_TIMEOUT_MS = 115_000;

const VALID_SEVERITIES: readonly A2pAnalysisSeverity[] = ['must_fix', 'recommended', 'other'];
const REDACTED_FIELDS = new Set([
  'firstName',
  'lastName',
  'jobPosition',
  'businessTitle',
  'email',
  'phoneNumber',
  'businessRegistrationNumber',
]);

@Injectable({ providedIn: 'root' })
export class A2pAnalysisService {
  analyse(rawValue: Record<string, unknown>): Observable<A2pAnalysis> {
    return generateChatAnswer$({
      assistantKey: { id: A2P_ASSISTANT_ID, namespace: { systemNamespace: {} } },
      chatHistory: [
        {
          role: ChatMessageRole.CHAT_MESSAGE_ROLE_USER,
          content: this.formatForm(rawValue),
          contentParts: [],
          externalId: '',
        },
      ],
      chatChannel: ChatChannel.CHAT_CHANNEL_PLATFORM,
      sessionId: '',
      invocationContext: {
        medium: InvocationContextMedium.TEXT,
        authState: InvocationContextAuthState.AUTHENTICATED,
        channel: InvocationContextChannel.PLATFORM,
      },
      options: { enableAsyncFunctions: false },
    }).pipe(
      timeout(ANALYSIS_TIMEOUT_MS),
      map((response) => this.parseResponse(response)),
    );
  }

  private parseResponse(response: GenerateChatAnswerResponse): A2pAnalysis {
    const toolCall = response.toolCalls?.find((call) => call.toolName === SUBMIT_A2P_ANALYSIS_TOOL);
    if (!toolCall?.arguments) {
      throw new Error('A2P analysis response did not include a SubmitA2PAnalysis tool call');
    }

    const parsed = JSON.parse(toolCall.arguments) as { Output?: string; items?: unknown[] };
    return {
      output: parsed.Output ?? '',
      items: (parsed.items ?? [])
        .map((item) => this.normalizeItem(item))
        .filter((item): item is A2pAnalysisItem => !!item),
    };
  }

  private normalizeItem(raw: unknown): A2pAnalysisItem | null {
    if (!raw || typeof raw !== 'object') return null;
    const item = raw as Record<string, unknown>;
    const severity = VALID_SEVERITIES.includes(item['severity'] as A2pAnalysisSeverity)
      ? (item['severity'] as A2pAnalysisSeverity)
      : 'other';
    return {
      severity,
      title: String(item['title'] ?? ''),
      description: String(item['description'] ?? ''),
      exampleWording: item['exampleWording'] ? String(item['exampleWording']) : undefined,
    };
  }

  private formatForm(rawValue: Record<string, unknown>): string {
    const lines: string[] = [];
    for (const [group, value] of Object.entries(rawValue)) {
      if (REDACTED_FIELDS.has(group)) continue;
      if (value && typeof value === 'object') {
        lines.push(`## ${group}`);
        for (const [field, fieldValue] of Object.entries(value as Record<string, unknown>)) {
          if (REDACTED_FIELDS.has(field)) continue;
          lines.push(`- ${field}: ${this.stringifyValue(fieldValue)}`);
        }
      } else {
        lines.push(`- ${group}: ${this.stringifyValue(value)}`);
      }
    }
    return lines.join('\n');
  }

  private stringifyValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '(empty)';
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
}
